/**
 * AchievementsPanel — stage-agnostic global panel.
 *
 * Lives on document.body so the same panel works in Phase 1 and Phase 2.
 * Two tabs: Achievements (earned/locked, grouped by category) and
 * Heritage Tree (8 nodes, spend Genome Shards to level them).
 *
 * Reads game data lazily through a getter — installed once at boot
 * before AssetLoader is done, so the panel asks for data the first time
 * the user opens it.
 *
 * Heritage purchases are NOT refundable (per design): the buy button
 * shows "MAX" once levels run out and there's no sell control.
 *
 * Earned achievements show a celebratory toast via the same DOM toast
 * stack that CodexSystem already uses. The panel itself just refreshes.
 */

import { bus } from '@/core/EventBus';
import { saveManager } from '@/core/SaveManager';
import type { GameData } from '@/core/AssetLoader';
import type { AchievementDef } from '@/data/types';
import { MetaUpgradeState } from '@/systems/MetaUpgradeState';

type Tab = 'achievements' | 'heritage';

interface PanelOptions {
  /** Returns the loaded game data. Called the first time the panel opens. */
  getData: () => GameData;
}

let installed = false;
let panelEl: HTMLElement | null = null;
let tabBar: HTMLElement | null = null;
let bodyEl: HTMLElement | null = null;
let shardLabel: HTMLElement | null = null;
let activeTab: Tab = 'achievements';
let getData: PanelOptions['getData'] | null = null;
let metaUpgrades: MetaUpgradeState | null = null;
let toastsRoot: HTMLElement | null = null;

export function installAchievementsPanel(opts: PanelOptions): void {
  if (installed) return;
  installed = true;
  getData = opts.getData;

  ensureToastsRoot();

  panelEl = document.createElement('div');
  panelEl.id = 'achievements-panel';
  panelEl.style.cssText =
    'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);' +
    'width:min(720px,92vw);max-height:min(620px,86vh);display:none;' +
    'background:rgba(8,12,18,0.96);border:1px solid rgba(180,220,230,0.25);' +
    'border-radius:14px;color:rgba(220,232,240,0.9);' +
    'font:13px/1.45 Inter,system-ui,sans-serif;' +
    'box-shadow:0 18px 60px rgba(0,0,0,0.7);z-index:9000;' +
    'flex-direction:column;overflow:hidden;';

  // header
  const header = document.createElement('div');
  header.style.cssText =
    'display:flex;align-items:center;justify-content:space-between;' +
    'padding:12px 16px;border-bottom:1px solid rgba(180,220,230,0.12);' +
    'background:rgba(20,30,40,0.55);';
  const title = document.createElement('div');
  title.textContent = 'Heritage';
  title.style.cssText = 'font-size:15px;font-weight:600;letter-spacing:0.04em;color:#fff;';
  header.appendChild(title);

  shardLabel = document.createElement('div');
  shardLabel.style.cssText = 'font-size:13px;color:#fadf7c;letter-spacing:0.02em;';
  header.appendChild(shardLabel);

  const closeBtn = document.createElement('button');
  closeBtn.textContent = '✕';
  closeBtn.style.cssText =
    'background:none;border:none;color:rgba(220,232,240,0.7);' +
    'font-size:16px;cursor:pointer;padding:4px 8px;margin-left:8px;';
  closeBtn.addEventListener('click', () => closePanel());
  header.appendChild(closeBtn);

  panelEl.appendChild(header);

  // tab bar
  tabBar = document.createElement('div');
  tabBar.style.cssText =
    'display:flex;border-bottom:1px solid rgba(180,220,230,0.12);' +
    'background:rgba(8,12,18,0.7);';
  const mkTab = (id: Tab, label: string): HTMLElement => {
    const b = document.createElement('button');
    b.dataset.tab = id;
    b.textContent = label;
    b.style.cssText =
      'flex:1;background:none;border:none;color:rgba(220,232,240,0.65);' +
      'padding:11px 14px;font:13px/1 Inter,system-ui,sans-serif;cursor:pointer;' +
      'border-bottom:2px solid transparent;letter-spacing:0.04em;';
    b.addEventListener('click', () => { activeTab = id; render(); });
    return b;
  };
  tabBar.appendChild(mkTab('achievements', 'Achievements'));
  tabBar.appendChild(mkTab('heritage', 'Heritage Tree'));
  panelEl.appendChild(tabBar);

  bodyEl = document.createElement('div');
  bodyEl.style.cssText = 'flex:1;overflow-y:auto;padding:14px 16px;';
  panelEl.appendChild(bodyEl);

  document.body.appendChild(panelEl);

  // earned-toast handler
  bus.on('achievement:earned', ({ title: t, reward }) => {
    showAchievementToast(t, reward);
    if (panelEl?.style.display === 'flex') render();
  });

  // ESC closes when open
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && panelEl?.style.display === 'flex') {
      closePanel();
    }
  });

  // Emit one shardsChanged so listeners that installed before us (e.g. the
  // global toolbar's heritage badge) get to evaluate affordability now
  // that getData is set.
  bus.emit('meta:shardsChanged', { shards: saveManager.getMeta().shards });
}

export function openPanel(): void {
  if (!panelEl) return;
  panelEl.style.display = 'flex';
  render();
  bus.emit('game:paused', undefined);
}

export function closePanel(): void {
  if (!panelEl) return;
  panelEl.style.display = 'none';
  bus.emit('game:resumed', undefined);
}

export function togglePanel(): void {
  if (!panelEl) return;
  if (panelEl.style.display === 'flex') closePanel();
  else openPanel();
}

/** True if the player can afford at least one un-maxed Heritage Tree level
 *  right now. Used by the toolbar to show a "!" prompt on the heritage
 *  button. Returns false until the panel has been installed and data is
 *  available, so callers can fire it before boot completes safely. */
export function hasAffordableHeritage(): boolean {
  if (!getData) return false;
  let data: GameData;
  try { data = getData(); }
  catch { return false; }
  if (!data?.metaUpgrades) return false;
  const meta = saveManager.getMeta();
  for (const def of Object.values(data.metaUpgrades)) {
    const lvl = meta.metaUpgrades[def.id] ?? 0;
    const next = def.costPerLevel[lvl];
    if (next != null && meta.shards >= next) return true;
  }
  return false;
}

// --------------------------------------------------------------- rendering

function render(): void {
  if (!bodyEl || !shardLabel || !tabBar || !getData) return;
  const meta = saveManager.getMeta();
  shardLabel.textContent = `🧬 ${meta.shards.toLocaleString()} Genome Shards`;

  // tab styles
  for (const t of Array.from(tabBar.children) as HTMLElement[]) {
    const isActive = t.dataset.tab === activeTab;
    t.style.color = isActive ? '#fff' : 'rgba(220,232,240,0.65)';
    t.style.borderBottomColor = isActive ? '#fadf7c' : 'transparent';
    t.style.background = isActive ? 'rgba(250,223,124,0.06)' : 'none';
  }

  bodyEl.innerHTML = '';
  if (activeTab === 'achievements') renderAchievements();
  else renderHeritage();
}

function renderAchievements(): void {
  if (!bodyEl || !getData) return;
  const data = getData();
  const meta = saveManager.getMeta();
  const earned = new Set(meta.achievements);

  const groups: Array<{ key: string; label: string }> = [
    { key: 'phase1', label: 'Phase 1 — Unicell' },
    { key: 'phase2', label: 'Phase 2 — Multicell' },
    { key: 'phase3', label: 'Phase 3 — Cambrian' },
    { key: 'cross', label: 'Cross-stage' },
    { key: 'mastery', label: 'Mastery (hidden)' },
  ];
  const byCat: Record<string, AchievementDef[]> = {};
  for (const def of Object.values(data.achievements)) {
    (byCat[def.category] ??= []).push(def);
  }
  for (const k of Object.keys(byCat)) {
    byCat[k].sort((a, b) => (b.reward - a.reward) || a.title.localeCompare(b.title));
  }

  const earnedCount = meta.achievements.length;
  const totalCount = Object.keys(data.achievements).length;
  const summary = document.createElement('div');
  summary.style.cssText = 'margin-bottom:14px;color:rgba(220,232,240,0.7);';
  summary.textContent = `${earnedCount} / ${totalCount} earned`;
  bodyEl.appendChild(summary);

  for (const { key, label } of groups) {
    const list = byCat[key] ?? [];
    if (list.length === 0) continue;
    const groupHdr = document.createElement('div');
    groupHdr.textContent = label;
    groupHdr.style.cssText =
      'font-size:11px;letter-spacing:0.18em;text-transform:uppercase;' +
      'color:rgba(180,220,230,0.6);margin:14px 0 6px;';
    bodyEl.appendChild(groupHdr);

    for (const def of list) {
      bodyEl.appendChild(achievementRow(def, earned.has(def.id)));
    }
  }
}

function achievementRow(def: AchievementDef, earned: boolean): HTMLElement {
  const row = document.createElement('div');
  row.style.cssText =
    'display:flex;align-items:center;gap:12px;padding:8px 10px;margin:4px 0;' +
    'background:rgba(20,30,40,0.45);border:1px solid rgba(180,220,230,0.08);' +
    'border-radius:8px;';
  if (earned) row.style.borderColor = 'rgba(250,223,124,0.5)';

  const icon = document.createElement('div');
  icon.textContent = earned ? '🏆' : (def.hidden ? '❓' : '◯');
  icon.style.cssText = 'font-size:20px;flex-shrink:0;width:26px;text-align:center;';
  if (!earned) icon.style.opacity = '0.45';
  row.appendChild(icon);

  const text = document.createElement('div');
  text.style.cssText = 'flex:1;min-width:0;';
  const t = document.createElement('div');
  const showHidden = def.hidden && !earned;
  t.textContent = showHidden ? '???' : def.title;
  t.style.cssText = `font-weight:600;color:${earned ? '#fff' : 'rgba(220,232,240,0.85)'};`;
  text.appendChild(t);
  const d = document.createElement('div');
  d.textContent = showHidden ? 'Hidden — discover this on your own.' : def.description;
  d.style.cssText = 'font-size:12px;color:rgba(220,232,240,0.55);margin-top:2px;';
  text.appendChild(d);
  row.appendChild(text);

  const reward = document.createElement('div');
  reward.textContent = `🧬 ${def.reward}`;
  reward.style.cssText =
    `flex-shrink:0;color:${earned ? '#fadf7c' : 'rgba(220,232,240,0.5)'};` +
    'font-weight:600;letter-spacing:0.02em;';
  row.appendChild(reward);

  return row;
}

function renderHeritage(): void {
  if (!bodyEl || !getData) return;
  const data = getData();
  if (!metaUpgrades) metaUpgrades = new MetaUpgradeState(data.metaUpgrades);

  const intro = document.createElement('div');
  intro.style.cssText = 'margin-bottom:12px;color:rgba(220,232,240,0.7);font-size:12px;';
  intro.textContent =
    'Spend Genome Shards on permanent buffs that carry across every run. ' +
    'Levels are NOT refundable — choose deliberately.';
  bodyEl.appendChild(intro);

  for (const def of Object.values(data.metaUpgrades)) {
    bodyEl.appendChild(heritageRow(def.id));
  }
}

function heritageRow(id: string): HTMLElement {
  const data = getData!();
  const def = data.metaUpgrades[id];
  const lvl = metaUpgrades!.levelOf(id);
  const max = def.costPerLevel.length;
  const isMax = metaUpgrades!.isMax(id);
  const cost = isMax ? 0 : metaUpgrades!.nextCost(id);
  const meta = saveManager.getMeta();
  const canAfford = !isMax && meta.shards >= cost;

  const row = document.createElement('div');
  row.style.cssText =
    'display:flex;align-items:center;gap:12px;padding:10px 12px;margin:6px 0;' +
    'background:rgba(20,30,40,0.45);border:1px solid rgba(180,220,230,0.1);' +
    'border-radius:8px;';

  const text = document.createElement('div');
  text.style.cssText = 'flex:1;min-width:0;';
  const t = document.createElement('div');
  t.style.cssText = 'font-weight:600;color:#fff;';
  t.textContent = def.title;
  text.appendChild(t);
  const d = document.createElement('div');
  d.style.cssText = 'font-size:12px;color:rgba(220,232,240,0.55);margin-top:2px;';
  d.textContent = def.description;
  text.appendChild(d);
  const lvlText = document.createElement('div');
  lvlText.style.cssText = 'font-size:11px;color:rgba(180,220,230,0.7);margin-top:4px;letter-spacing:0.04em;';
  lvlText.textContent = `Level ${lvl} / ${max}`;
  text.appendChild(lvlText);
  row.appendChild(text);

  const buy = document.createElement('button');
  buy.style.cssText =
    'flex-shrink:0;padding:8px 14px;border-radius:6px;border:1px solid rgba(180,220,230,0.25);' +
    'background:rgba(20,30,40,0.7);color:#fff;font:13px/1 Inter,system-ui,sans-serif;' +
    'cursor:pointer;letter-spacing:0.02em;';
  if (isMax) {
    buy.textContent = 'MAX';
    buy.style.opacity = '0.4';
    buy.style.cursor = 'default';
    buy.disabled = true;
  } else {
    buy.textContent = `🧬 ${cost}`;
    buy.style.color = canAfford ? '#fadf7c' : 'rgba(220,232,240,0.4)';
    buy.style.borderColor = canAfford ? 'rgba(250,223,124,0.4)' : 'rgba(180,220,230,0.15)';
    if (!canAfford) buy.disabled = true;
  }
  buy.addEventListener('click', () => {
    if (metaUpgrades!.buy(id)) render();
  });
  row.appendChild(buy);

  return row;
}

// --------------------------------------------------------------- toast

function ensureToastsRoot(): void {
  if (toastsRoot) return;
  toastsRoot = document.getElementById('global-toasts');
  if (toastsRoot) return;
  toastsRoot = document.createElement('div');
  toastsRoot.id = 'global-toasts';
  toastsRoot.style.cssText =
    'position:fixed;top:80px;right:20px;display:flex;flex-direction:column;gap:8px;' +
    'z-index:9500;pointer-events:none;';
  document.body.appendChild(toastsRoot);
}

function showAchievementToast(title: string, reward: number): void {
  ensureToastsRoot();
  if (!toastsRoot) return;
  const t = document.createElement('div');
  t.style.cssText =
    'background:rgba(8,12,18,0.95);border:1px solid rgba(250,223,124,0.55);' +
    'border-radius:10px;padding:10px 14px;color:#fff;' +
    'font:13px/1.3 Inter,system-ui,sans-serif;letter-spacing:0.02em;' +
    'box-shadow:0 8px 30px rgba(0,0,0,0.5);min-width:240px;' +
    'opacity:0;transform:translateX(40px);transition:opacity 0.4s,transform 0.4s;';
  t.innerHTML =
    `<div style="font-size:11px;color:rgba(250,223,124,0.85);letter-spacing:0.18em;text-transform:uppercase;">Achievement</div>` +
    `<div style="font-weight:600;margin-top:2px;">${escape(title)}</div>` +
    `<div style="font-size:12px;margin-top:4px;color:#fadf7c;">+${reward} 🧬 Genome Shard${reward === 1 ? '' : 's'}</div>`;
  toastsRoot.appendChild(t);
  requestAnimationFrame(() => {
    t.style.opacity = '1';
    t.style.transform = 'translateX(0)';
  });
  setTimeout(() => {
    t.style.opacity = '0';
    t.style.transform = 'translateX(40px)';
  }, 4200);
  setTimeout(() => t.remove(), 4800);
}

function escape(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}
